import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const EMPTY = { title: "", image: "", file: "", category: "arcade", description: "", instructions: "", w: 800, h: 600, tags: [], video: "", mobile: true, published: true, featured: false };
const inputCls = "bg-white/5 text-white border-white/15";

export function GameForm({ game, categories, onClose, onSaved }) {
  const isNew = !game.slug;
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/games/${game.slug}`).then((r) => setF({ ...EMPTY, ...r.data })).catch(() => {});
  }, [game.slug, isNew]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, w: Number(f.w) || 800, h: Number(f.h) || 600, tags: typeof f.tags === "string" ? f.tags.split(",").map((t) => t.trim()).filter(Boolean) : f.tags };
    try {
      if (isNew) await api.post("/admin/games", body);
      else {
        const { slug, gid, plays, likes, dislikes, date_added, featured_order, ...rest } = body;
        await api.patch(`/admin/games/${game.slug}`, rest);
      }
      toast.success(isNew ? "Gioco aggiunto" : "Gioco salvato");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  const field = (k, label, props = {}) => (
    <div className="space-y-1">
      <Label className="text-slate-200">{label}</Label>
      <Input value={f[k] ?? ""} onChange={set(k)} className={inputCls} data-testid={`game-form-${k}`} {...props} />
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="gg-admin max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0d2233] border-white/10 text-white" data-testid="game-form-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Aggiungi gioco" : "Modifica gioco"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {field("title", "Titolo", { required: true })}
          <div className="grid sm:grid-cols-2 gap-4">
            {field("image", "URL immagine (512x384)", { required: true })}
            {field("file", "URL gioco (iframe)", { required: true })}
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-slate-200">Categoria</Label>
              <Select value={f.category} onValueChange={set("category")}>
                <SelectTrigger className={inputCls} data-testid="game-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {field("w", "Larghezza", { type: "number" })}
            {field("h", "Altezza", { type: "number" })}
          </div>
          {field("video", "URL video anteprima (mp4, opzionale)")}
          <div className="space-y-1">
            <Label className="text-slate-200">Tag (separati da virgola)</Label>
            <Input value={Array.isArray(f.tags) ? f.tags.join(", ") : f.tags} onChange={set("tags")} className={inputCls} data-testid="game-form-tags" />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-200">Descrizione</Label>
            <Textarea rows={5} value={f.description} onChange={set("description")} className={inputCls} data-testid="game-form-description" />
          </div>
          {field("instructions", "Istruzioni")}
          <div className="flex flex-wrap gap-6">
            {[["published", "Pubblicato"], ["featured", "In evidenza"], ["mobile", "Compatibile mobile"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={!!f[k]} onCheckedChange={set(k)} data-testid={`game-form-${k}`} /> {l}</label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="hover:bg-white/10 hover:text-white">Annulla</Button>
            <Button type="submit" disabled={busy} className="bg-[#fc0] text-[#043856] hover:bg-yellow-300 font-bold" data-testid="game-form-submit">{busy ? "Salvataggio..." : "Salva"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
